import { useState, useEffect } from "react";
import {
  Bell,
  Search,
  Settings,
  LogOut,
  User,
  ChevronDown,
  Check,
  Shield,
  Trash2,
  CheckCheck,
  Info,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  X,
  ShoppingCart,
  Wrench,
  Lock,
  Unlock,
  ShieldCheck,
  Megaphone,
  MoreHorizontal
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSuperAdmin } from "@/hooks/useSuperAdmin";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useSettings } from "@/hooks/useSettings";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { TwoFactorSetup } from "@/components/auth/TwoFactorSetup";
import { useNotifications, Notification } from "@/hooks/useNotifications";
import { useSecurityFindingsAlerts } from "@/hooks/useSecurityFindingsAlerts";
import { useTheme } from "@/contexts/ThemeContext";
import { WorkspaceSelector } from "./WorkspaceSelector";
import { LanguageSwitcher } from "@/components/public/LanguageSwitcher";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { ShieldAlert } from "lucide-react";

const notificationIcons = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
};

interface Profile {
  full_name: string | null;
  avatar_url: string | null;
  role: string | null;
  two_factor_enabled: boolean;
}

export function AppHeader() {
  const { workspace, currentPlan } = useWorkspace();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState<string | null>(null);
  
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { 
    notifications, 
    unreadCount, 
    loading: notificationsLoading,
    markAsRead, 
    markAllAsRead, 
    deleteNotification,
    clearAll 
  } = useNotifications();
  const { isSuperAdmin } = useSuperAdmin();
  const {
    items: securityAlerts,
    unreadCount: securityUnread,
    isSuperAdmin: isSecurityViewer,
  } = useSecurityFindingsAlerts();
  const { settings: securitySettings } = useSettings("security");
  const isProtectionEnabled = !!securitySettings.rightClickProtection;

  const userId = user?.id;
  
  useEffect(() => {
    if (userId) {
      fetchProfile();
      fetchCompanySettings();
    }
  }, [userId]);

  const fetchProfile = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("profiles")
      .select("full_name, avatar_url, role, two_factor_enabled")
      .eq("user_id", user.id)
      .maybeSingle();
    if (data) setProfile(data);
  };

  const fetchCompanySettings = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("settings")
      .select("settings")
      .eq("user_id", user.id)
      .eq("category", "general")
      .maybeSingle();
    if (data?.settings && typeof data.settings === "object" && !Array.isArray(data.settings)) {
      const s = data.settings as Record<string, unknown>;
      setCompanyLogo((s.logoUrl as string) || null);
      setCompanyName((s.companyName as string) || null);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const displayName = companyName || profile?.full_name || user?.email?.split("@")[0] || "Usuario";
  const displayRole = profile?.role || "Usuario";
  const initials = displayName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);

  const quickActions = [
    { icon: ShoppingCart, label: "POS", title: "Punto de Venta", href: "/finance/pos" },
    { icon: Wrench, label: "Órdenes", title: "Órdenes de Trabajo", href: "/workshop/orders" },
    { icon: ShieldCheck, label: "Seguros", title: "Seguros", href: "/insurance", guard: currentPlan === "enterprise" || isSuperAdmin },
    { icon: Megaphone, label: "Campañas", title: "Campañas de Notificación", href: "/crm/campaigns" },
  ];

  return (
    <>
      <header className="sticky top-0 z-30 flex min-h-[56px] sm:h-16 items-center gap-2 sm:gap-4 border-b border-border bg-background/95 px-3 sm:px-6 py-2 sm:py-0 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <WorkspaceSelector />

          {/* Desktop search */}
          <div className="relative hidden md:flex flex-1 max-w-md lg:max-w-xl">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar en el sistema..."
              className="w-full pl-10 h-9 sm:h-10 bg-secondary/70 border-border/60 hover:bg-secondary focus:bg-background focus:border-primary transition-colors"
            />
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {/* Mobile search */}
          <div className="md:hidden">
            <Drawer open={searchOpen} onOpenChange={setSearchOpen}>
              <DrawerTrigger asChild>
                <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground hover:bg-secondary h-9 w-9">
                  <Search className="h-5 w-5" />
                </Button>
              </DrawerTrigger>
              <DrawerContent className="px-4 pb-6">
                <DrawerHeader className="flex items-center justify-between px-0">
                  <DrawerTitle>Buscar</DrawerTitle>
                  <DrawerClose asChild>
                    <Button variant="ghost" size="icon">
                      <X className="h-4 w-4" />
                    </Button>
                  </DrawerClose>
                </DrawerHeader>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Buscar en el sistema..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 bg-secondary border-border focus:border-primary"
                    autoFocus
                  />
                </div>
              </DrawerContent>
            </Drawer>
          </div>

          {/* Quick actions pill */}
          <div className="hidden sm:flex items-center bg-secondary/60 rounded-xl p-1 border border-border/50">
            {quickActions.map((action) => {
              if (action.guard === false) return null;
              const ActionIcon = action.icon;
              return (
                <Button
                  key={action.href}
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(action.href)}
                  className="text-muted-foreground hover:text-foreground hover:bg-background h-8 px-2 lg:px-2.5 gap-1.5 rounded-lg"
                  title={action.title}
                >
                  <ActionIcon className="h-4 w-4" />
                  <span className="hidden lg:inline text-xs font-medium">{action.label}</span>
                </Button>
              );
            })}
          </div>

          {/* Mobile quick actions menu */}
          <div className="sm:hidden">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground hover:bg-secondary h-9 w-9">
                  <MoreHorizontal className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Accesos rápidos</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {quickActions.map((action) => {
                  if (action.guard === false) return null;
                  const ActionIcon = action.icon;
                  return (
                    <DropdownMenuItem key={action.href} onClick={() => navigate(action.href)}>
                      <ActionIcon className="mr-2 h-4 w-4" />
                      <span>{action.title}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {isSuperAdmin && (
            <Badge 
              variant="outline" 
              className={cn(
                "hidden lg:flex items-center gap-1.5 px-2 py-1 h-9 border-dashed",
                isProtectionEnabled ? "text-amber-500 border-amber-500/50" : "text-muted-foreground border-border"
              )}
              title={isProtectionEnabled ? "Protección de click derecho activa (No aplica para ti)" : "Protección de click derecho desactivada"}
            >
              {isProtectionEnabled ? (
                <>
                  <Lock className="h-3.5 w-3.5" />
                  <span className="text-[10px] font-bold uppercase tracking-wider">Protección ON</span>
                </>
              ) : (
                <>
                  <Unlock className="h-3.5 w-3.5" />
                  <span className="text-[10px] font-bold uppercase tracking-wider">Protección OFF</span>
                </>
              )}
            </Badge>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative text-muted-foreground hover:text-foreground hover:bg-secondary h-9 w-9">
                <Bell className="h-5 w-5" />
                {(unreadCount + securityUnread) > 0 && (
                  <Badge className="absolute -right-1 -top-1 h-5 w-5 rounded-full p-0 flex items-center justify-center bg-destructive text-destructive-foreground text-xs">
                    {unreadCount + securityUnread}
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 bg-popover border-border">
              <DropdownMenuLabel className="flex items-center justify-between">
                <span>Notificaciones</span>
                <div className="flex gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs text-primary hover:text-primary/80 h-6 px-2"
                    onClick={markAllAsRead}
                    disabled={unreadCount === 0}
                  >
                    <CheckCheck className="h-3 w-3 mr-1" />
                    Marcar leídas
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs text-destructive hover:text-destructive/80 h-6 px-2"
                    onClick={clearAll}
                    disabled={notifications.length === 0}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {isSecurityViewer && securityAlerts.length > 0 && (
                <>
                  <div className="px-3 pt-2 pb-1 flex items-center justify-between">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-destructive">
                      <ShieldAlert className="h-3.5 w-3.5" />
                      Alertas de seguridad
                    </div>
                  </div>
                  {securityAlerts.slice(0, 3).map((a) => (
                    <DropdownMenuItem
                      key={`sec-${a.id}`}
                      className={cn(
                        "flex flex-col items-start gap-1 p-3 cursor-pointer border-l-2",
                        a.severity === "critical" && "border-destructive bg-destructive/5",
                        a.severity === "high" && "border-orange-500 bg-orange-500/5",
                        a.severity === "medium" && "border-amber-400",
                        (a.severity === "low" || a.severity === "info") && "border-muted",
                      )}
                      onClick={() => navigate(`/admin/security-findings?id=${a.id}`)}
                    >
                      <div className="flex items-start gap-2 w-full">
                        <ShieldAlert className={cn(
                          "h-4 w-4 mt-0.5 shrink-0",
                          a.severity === "critical" || a.severity === "high"
                            ? "text-destructive"
                            : "text-muted-foreground"
                        )} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{a.title}</p>
                          <p className="text-xs text-muted-foreground line-clamp-1">{a.source}</p>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  ))}
                  <DropdownMenuSeparator />
                </>
              )}
              <div className="max-h-80 overflow-y-auto">
                {notificationsLoading ? (
                  <div className="p-4 text-center text-muted-foreground text-sm">Cargando...</div>
                ) : notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                    <Bell className="h-10 w-10 mb-2 opacity-20" />
                    <p className="text-sm">No tienes notificaciones</p>
                    <p className="text-xs opacity-50">¡Todo al día!</p>
                  </div>
                ) : (

                  notifications.map((notification) => {
                    const IconComponent = notificationIcons[notification.type as keyof typeof notificationIcons] || Info;
                    return (
                      <DropdownMenuItem
                        key={notification.id}
                        className={cn(
                          "flex flex-col items-start gap-1 p-3 cursor-pointer",
                          !notification.read && "bg-primary/5"
                        )}
                        onClick={() => {
                          if (!notification.read) markAsRead(notification.id);
                          if (notification.link) navigate(notification.link);
                        }}
                      >
                        <div className="flex items-start gap-2 w-full">
                          <IconComponent className={cn(
                            "h-4 w-4 mt-0.5 shrink-0",
                            notification.type === 'success' && "text-green-500",
                            notification.type === 'warning' && "text-amber-500",
                            notification.type === 'error' && "text-destructive",
                            notification.type === 'info' && "text-primary"
                          )} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium truncate">{notification.title}</p>
                              {!notification.read && <div className="h-2 w-2 rounded-full bg-primary shrink-0" />}
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-2">{notification.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: es })}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                            aria-label="Eliminar notificación"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </DropdownMenuItem>
                    );
                  })
                )}
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="flex items-center justify-center py-2 font-medium text-primary cursor-pointer"
                onClick={() => navigate('/notifications')}
              >
                Ver todas las notificaciones
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-8 w-8 sm:h-9 sm:w-9 rounded-full hover:bg-secondary p-0">
                <div className="flex h-full w-full items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-[10px] sm:text-xs ring-2 ring-transparent hover:ring-primary/20 transition-all">
                  {initials}
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{displayName}</p>
                  <p className="text-xs leading-none text-muted-foreground">{user?.email}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/settings/general")}>
                <User className="mr-2 h-4 w-4" />
                <span>Mi Perfil</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/settings/security")}>
                <Lock className="mr-2 h-4 w-4" />
                <span>Seguridad</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
                <LogOut className="mr-2 h-4 w-4" />
                <span>Cerrar Sesión</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
    </>
  );
}
